import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, File, ArrowLeft } from 'lucide-react';
import { toast } from '../ui/Toast';

const ACCEPT = '.mp3,.wav,.m4a,.webm,.ogg,.flac';

const VoiceFileUpload = ({ onResult, onBack }) => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [action, setAction] = useState('transcribe');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (f && f.size <= 25 * 1024 * 1024) {
      setFile(f);
    } else if (f) {
      toast.warning('Fichier trop volumineux (max 25 Mo)');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const processFile = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', 'fr');

      const transcribeRes = await axios.post('/api/voice/upload', formData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });

      const transcribedText = transcribeRes.data.text;

      if (action === 'transcribe') {
        onResult(transcribedText, 'Transcription');
      } else {
        const endpoint = action === 'summarize' ? '/api/voice/summarize' : '/api/voice/rewrite';
        const aiRes = await axios.post(endpoint, { text: transcribedText }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 60000,
        });
        onResult(aiRes.data.markdown, action === 'summarize' ? 'Résumé' : 'Réécriture');
      }
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  if (processing) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #475569', borderTopColor: '#f97316', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Traitement du fichier...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={styles.backBtn}>
        <ArrowLeft size={14} /> Retour
      </button>

      {/* Action selector */}
      <div style={styles.actionRow}>
        {[['transcribe', 'Transcrire'], ['summarize', 'Résumer'], ['rewrite', 'Réécrire']].map(([key, label]) => (
          <button key={key} onClick={() => setAction(key)}
            style={{ ...styles.actionBtn, ...(action === key ? styles.actionActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}), ...(file ? styles.dropZoneFile : {}) }}
      >
        <input ref={inputRef} type="file" accept={ACCEPT} hidden
          onChange={(e) => handleFile(e.target.files[0])} />
        {file ? (
          <>
            <File size={24} color="#f97316" />
            <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 500 }}>{file.name}</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>{formatSize(file.size)}</div>
          </>
        ) : (
          <>
            <Upload size={28} color="#64748b" />
            <div style={{ color: '#94a3b8', fontSize: 14 }}>Glissez un fichier audio ici</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>MP3, WAV, M4A, WebM, OGG (max 25 Mo)</div>
          </>
        )}
      </div>

      {file && (
        <button onClick={processFile} style={styles.processBtn}>
          Traiter le fichier
        </button>
      )}
    </div>
  );
};

const styles = {
  backBtn: {
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 16, padding: 0,
  },
  actionRow: { display: 'flex', gap: 6, marginBottom: 16 },
  actionBtn: {
    flex: 1, padding: '8px 0', borderRadius: 6, border: '1px solid #475569',
    background: '#334155', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  },
  actionActive: { background: '#f97316', color: 'white', borderColor: '#f97316' },
  dropZone: {
    border: '2px dashed #475569', borderRadius: 12, padding: '32px 16px',
    textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
  },
  dropZoneActive: { borderColor: '#f97316', background: 'rgba(249,115,22,0.05)' },
  dropZoneFile: { borderColor: '#f97316', borderStyle: 'solid' },
  processBtn: {
    width: '100%', marginTop: 12, padding: '12px 0', borderRadius: 8, border: 'none',
    background: '#f97316', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
};

export default VoiceFileUpload;
